import React, { useState } from 'react';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import type { SpecDrawerProps } from '../SpecDrawer/SpecDrawer';
import { Comment, CommentProps } from './Comment';
import { CommentReplyBar } from './CommentReplyBar';
import { XIcon } from '../icons';
import './Comment.css';

type DrawerSize = 'minimized' | 'default' | 'large';

export interface CommentDrawerProps {
  /**
   * Whether the drawer is open
   */
  open: boolean;
  /**
   * Callback when the drawer is closed
   */
  onClose: () => void;
  /**
   * Array of comment data to display
   */
  comments: Omit<CommentProps, 'className'>[];
  /**
   * Title for the drawer (default: "Comments")
   */
  title?: string;
  /**
   * Initial drawer size (default: "large")
   */
  initialSize?: DrawerSize;
  /**
   * Callback when the reply bar is clicked (opens add-comment flow)
   */
  onAddComment?: () => void;
  /**
   * Callback when a quick emoji is tapped on the reply bar
   */
  onEmojiClick?: (emoji: string) => void;
  /**
   * Additional props to pass to SpecDrawer
   */
  drawerProps?: Partial<SpecDrawerProps>;
  /**
   * Fullscreen mode - drawer opens fully expanded, no dragging, X button to close
   */
  fullscreen?: boolean;
}

/**
 * CommentDrawer - A drawer showing all comments
 * 
 * Opens as a large drawer with all comments displayed in a scrollable list.
 */
export const CommentDrawer: React.FC<CommentDrawerProps> = ({
  open,
  onClose,
  comments,
  title = 'Comments',
  initialSize = 'large',
  onAddComment,
  onEmojiClick,
  drawerProps,
  fullscreen = false,
}) => {
  const [drawerSize, setDrawerSize] = useState<DrawerSize>(fullscreen ? 'large' : initialSize);

  // Custom title with X button
  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      {/* Drag handle placeholder to maintain spacing */}
      <div 
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      {title}
      {/* X close button - absolutely positioned */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '8px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close drawer"
          className="drawer-close-button"
        >
          <XIcon size="m" tone="white" />
        </button>
      </div>
    </div>
  );

  return (
    <SpecDrawer
      title={customTitle}
      open={open}
      onClose={onClose}
      size={fullscreen ? 'large' : drawerSize}
      onSizeChange={fullscreen ? undefined : setDrawerSize}
      swipeable={false}
      closable={false}
      footer={onAddComment ? (
        <div className="comment-drawer-footer">
          <CommentReplyBar onClick={onAddComment} onEmojiClick={onEmojiClick} />
        </div>
      ) : undefined}
      {...drawerProps}
    >
      {(fullscreen || drawerSize !== 'minimized') && (
        <div className="comment-drawer-content">
          {comments.length === 0 ? (
            <div className="comment-list-empty">
              No chats yet. Be the first to chat!
            </div>
          ) : (
            <div className="comment-drawer-list">
              {comments.map((comment, index) => (
                <Comment key={index} {...comment} />
              ))}
            </div>
          )}
        </div>
      )}
    </SpecDrawer>
  );
};

export default CommentDrawer;
